import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ObfuscatedEmail from '@/components/base/ObfuscatedEmail';

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const { t, i18n } = useTranslation();

  const currentLang = i18n.language === 'fr' ? 'fr' : 'en';

  const toggleLanguage = () => {
    i18n.changeLanguage(currentLang === 'en' ? 'fr' : 'en');
  };

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const links = [
    { labelKey: 'nav_home', path: '/' },
    { labelKey: 'nav_contact', path: '/contact' },
  ];

  const textColor = (active: boolean) => {
    if (active) return 'text-green-500';
    return scrolled ? 'text-gray-700 hover:text-green-600' : 'text-white hover:text-green-300';
  };

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled ? 'bg-white shadow-sm' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between h-20">
        {/* Logo */}
        <Link to="/" className="flex items-center">
          <div className="bg-white rounded-lg px-3 py-1">
            <img
              src="https://storage.readdy-site.link/project_files/766ecf38-5aae-4b99-ad7c-3b9a28df6201/4b39bf62-c5e4-4bc8-a85b-8ffbe72ab4ca_VixtraLogo.png?v=a7e79daeb5093b144c08c9d5f97ff157"
              alt="Vixtra Inc."
              className="h-14 w-auto object-contain"
            />
          </div>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8">
          {links.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`text-sm font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer ${textColor(location.pathname === link.path)}`}
            >
              {t(link.labelKey)}
            </Link>
          ))}
          <ObfuscatedEmail
            label={t('nav_get_in_touch')}
            className="whitespace-nowrap bg-green-600 hover:bg-green-700 text-white text-sm font-medium px-5 py-2 rounded-md transition-colors duration-200"
          />

          {/* Language Toggle */}
          <button
            onClick={toggleLanguage}
            className={`whitespace-nowrap cursor-pointer flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-md border transition-colors duration-200 ${
              scrolled
                ? 'border-stone-300 text-stone-700 hover:border-green-500 hover:text-green-600'
                : 'border-white/50 text-white hover:border-white'
            }`}
            aria-label="Toggle language"
          >
            <span className="text-xs">{currentLang === 'en' ? '🇫🇷' : '🇨🇦'}</span>
            <span>{currentLang === 'en' ? 'FR' : 'EN'}</span>
          </button>
        </nav>

        {/* Mobile Hamburger */}
        <button
          className="md:hidden flex items-center justify-center w-10 h-10 cursor-pointer"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          <i className={`${menuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl ${scrolled ? 'text-gray-800' : 'text-white'}`}></i>
        </button>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 px-6 py-4 flex flex-col gap-4">
          {links.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              onClick={() => setMenuOpen(false)}
              className={`text-sm font-medium cursor-pointer ${
                location.pathname === link.path ? 'text-green-600' : 'text-gray-700 hover:text-green-600'
              }`}
            >
              {t(link.labelKey)}
            </Link>
          ))}
          <ObfuscatedEmail
            label={t('nav_get_in_touch')}
            className="whitespace-nowrap bg-green-600 text-white text-sm font-medium px-5 py-2 rounded-md text-center"
          />
          <button
            onClick={() => { toggleLanguage(); setMenuOpen(false); }}
            className="whitespace-nowrap cursor-pointer flex items-center gap-2 text-sm font-semibold text-stone-700 hover:text-green-600 transition-colors"
          >
            <span>{currentLang === 'en' ? '🇫🇷' : '🇨🇦'}</span>
            <span>{currentLang === 'en' ? 'Français' : 'English'}</span>
          </button>
        </div>
      )}
    </header>
  );
};

export default Navbar;
